// @flow
import * as React from 'react';

import { Reshaped, useTheme, type ReshapedProps } from 'reshaped';

import { useLayoutEffect } from 'src/hooks/useLayoutEffect';

import styles from './ReshapedGandiProvider.module.css';

/**
 * Ensure Reshaped styles are applied to the server side render, preventing FOUC.
 * But without leaking on the whole application as if set on the `html` element.
 * The `Reshaped` component does not provide those attributes server side since
 * it's using effects/layoutEffects internally.
 */

type Props = {
  ...ReshapedProps,
  initialColorMode?: 'light' | 'dark',
};

function ColorModeSync({
  rootRef,
}: {
  rootRef: { current: null | HTMLElement },
}): null {
  const { colorMode } = useTheme();

  useLayoutEffect(() => {
    rootRef.current?.setAttribute('data-rs-color-mode', colorMode);
  }, [colorMode, rootRef]);

  return null;
}

export function ReshapedGandiProvider({
  initialColorMode = 'light',
  children,
  ...rest
}: Props): React.MixedElement {
  const rootRef = React.useRef<null | HTMLElement>(null);

  return (
    <div
      ref={rootRef}
      data-rs-theme="gandi"
      data-rs-color-mode={initialColorMode}
      className={styles.root}
    >
      <Reshaped
        defaultTheme="gandi"
        defaultColorMode={initialColorMode}
        {...rest}
      >
        <ColorModeSync rootRef={rootRef} />
        {children}
      </Reshaped>
    </div>
  );
}
